import { Chip, Stack, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useMemo, useState } from "react";
import useOpen from "utils/useOpen";
import CompetenciesDialog from "./CompetenciesDialog";
import competencyService from "services/competency-service";
import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";

export default function AllCompetencies({ permissions }) {
  const { label } = useLanguage();
  const open = useOpen({ manageDialog: false, alert: false });
  const can = usePermissionMonitor();

  const [competencies, setCompetencies] = useState(null);
  const [lastFilter, setLastFilter] = useState(null);

  async function handleAddCompetency(newElement) {
    const [response, data] = await competencyService.createCompetency({
      name: newElement.name,
      accountId: newElement.accountId || null,
      referenceCode: newElement.referenceCode,
    });
    if (response.ok) {
      const [response, data] =
        await competencyService.getCompetencies(lastFilter);
      if (response.ok) {
        setCompetencies(data);
      }
      open.change("create", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateCompetency(newElement) {
    const [response, data] = await competencyService.updateCompetency(
      newElement.id,
      {
        name: newElement.name,
        accountId: newElement.accountId || null,
        referenceCode: newElement.referenceCode,
      },
    );
    if (response.ok) {
      const [response, data] =
        await competencyService.getCompetencies(lastFilter);
      if (response.ok) {
        setCompetencies(data);
      }
      open.change("update", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteCompetency(id) {
    const [response, data] = await competencyService.removeCompetency(id);
    if (response.ok) {
      const [response, data] =
        await competencyService.getCompetencies(lastFilter);
      if (response.ok) {
        setCompetencies(data);
      }
      open.change("alert", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  const rows = useMemo(() => {
    if (!competencies?.length)
      return competencies?.length === 0
        ? [
            {
              name: label("no-competencies-were-found"),
            },
          ]
        : [
            {
              name: "Loading...",
            },
          ];
    return competencies.map((competency) => {
      return {
        name: <MDTypography variant="inherit">{competency.name}</MDTypography>,
        referenceCode: (
          <MDTypography variant="inherit">
            {competency.referenceCode || label("none")}
          </MDTypography>
        ),
        account: (
          <MDTypography variant="inherit">
            {competency.account?.name || label("all")}
          </MDTypography>
        ),

        actions:
          (!competency.accountId && can("isAdmin")) || competency.accountId ? (
            <MDBox
              display="flex"
              flexDirection="row"
              justifyContent="end"
              alignItems="center"
            >
              {permissions.update && (
                <Tooltip title={label("Edit")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("update", {
                        competency,
                      });
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {permissions.delete && (
                <Tooltip title={label("Delete")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("alert", {
                        competency,
                      });
                    }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          ) : null,
      };
    });
  }, [competencies, permissions]);

  const columns = useMemo(() => {
    const columns = [
      {
        Header: label("name-singular"),
        accessor: "name",
      },
      {
        Header: label("reference-code-singular"),
        accessor: "referenceCode",
      },
      {
        Header: label("account-singular"),
        accessor: "account",
        width: "100%",
      },
    ];

    if (permissions.update || permissions.delete) {
      columns.push({
        Header: "",
        accessor: "actions",
        align: "right",
        width: "100%",
      });
    }

    return columns;
  }, [permissions]);

  return (
    <>
      {open.get("alert") ? (
        <AlertDialog
          open={Math.random()}
          setOpen={(v) => {
            open.change("alert", v);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await handleDeleteCompetency(open.get("alert").competency.id);
          }}
        />
      ) : null}
      <CompetenciesDialog
        mode="create"
        isOpen={open.get("create") ? true : false}
        onSubmit={handleAddCompetency}
        onClose={() => open.change("create", false)}
        data={null}
      />
      <CompetenciesDialog
        mode="edit"
        isOpen={open.get("update") ? true : false}
        onSubmit={handleUpdateCompetency}
        onClose={() => open.change("update", false)}
        data={open.get("update").competency}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("competency-plural")}{" "}
                  {permissions.get ? `(${competencies?.length || 0})` : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("create", true);
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-competency-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-competencies"
                      internalLoading={!Boolean(competencies)}
                      fullWidth
                      setData={setCompetencies}
                      onSearch={async (request, callback) => {
                        setLastFilter(request);
                        callback(
                          await competencyService.getCompetencies(request),
                        );
                      }}
                      label={label("search-competencies-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(competencies)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
